import { type FC, memo } from 'react'
import { useParams } from 'next/navigation'
import Link from 'next/link'
import styled from 'styled-components'
import { MODALS } from '@/context'
import { useModal, useTranslator } from '@/hooks'
import { LOCALE, LANGUAGES} from '@/utils'
import type { TypeLang, TypeCmsLayout } from '@/schema'
import { createUnderline, defaultGrid, designGrid, getP18, mq, vw } from '@/styles'
import { PreFooter } from '@/components/PreFooter'

type Props = {
  data: TypeCmsLayout['footer']
}

const getLinksGrouped = (data: TypeCmsLayout['footer']['cols']) => data.map((group, idx) => {
  return (
    <div key={idx}>
      <dt id={`footer-nav-${idx}`} className='srt-only'>Footer site links</dt>
      {group.map((item, index) => {
        const href = `/${item.url.page}${item.url.slug ? `/${item.url.slug}`: ''}`

        return (
          <dd key={index}>
            <Link href={href} target='_blank' rel='noreferrer'>{item.title === 'Culture x Light' || item.title === 'Cultura x Luce' || item.title === 'Culture x Lumière'  ? 'Manifesto' : item.title}</Link>
          </dd>
        )
      })}
    </div>
  )
})


export const Footer:FC<Props> = memo(({ data }) => {
  const { cols } = data
  const { toggle, isActive } = useModal(MODALS.CONTACT)
  const { t } = useTranslator()
  const { lang }: { lang:TypeLang } = useParams()

  return (
    <>
      <PreFooter />
      <FooterStyled>
        <section aria-labelledby='footer-contact'>
          <h2 id='footer-contact' className='srt-only'>Contact info</h2>
          <button type='button' className='btn--contact' onClick={toggle} aria-haspopup='dialog' aria-controls={MODALS.CONTACT} aria-expanded={isActive} dangerouslySetInnerHTML={{ __html: t('project.footer.contact.title') as string }} />
          <address>
            <a href='mailto:info@nexia.es'>
              {t('project.footer.contact.mail')}<br /><span>info@nexia.es</span></a>
            <a href='tel:+34937154712'>{t('project.footer.contact.phone')}<br /><span>+34 93 715 47 12</span></a>
            <a href='https://maps.app.goo.gl/SHznkVtKs9nD3mHFA' target='_blank' rel='noopener noreferrer'>
              {t('project.footer.contact.address')}<br />
              <span>
                Carrer del Priorat, 14-20,<br />08211 Castellar del<br />Vallès, Barcelona
              </span>
            </a>
          </address>
        </section>
        <nav aria-labelledby='footer-nav-0'>
          <dl>
            {getLinksGrouped(cols)}
          </dl>
        </nav>
        <div>
          <ul className='list--langs'>
            {LANGUAGES.map((item, idx) => (
              <li key={idx}>
                <Link href={`/${item === LOCALE ? '' : item}`} className={`${lang === item ? 'active' : ''}`}>
                  {item}
                </Link>
              </li>
            ))}
          </ul>
          <ul className='list--rrss'>
            <li>
              <a href='https://www.instagram.com/nexialighting/' rel='noreferrer' target='_blank' title='instagram'>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src='/images/svg/i--rrss--instagram.svg' alt='Instagram logo' />
              </a>
            </li>
            <li>
              <a href='https://es.linkedin.com/company/nexialighting' rel='noreferrer' target='_blank' title='linkedin'>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src='/images/svg/i--rrss--linkedin.svg' alt='LinkedIn logo' />
              </a>
            </li>
            <li>
              <a href='https://www.pinterest.es/nexialighting/' rel='noreferrer' target='_blank' title='pinterest'>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src='/images/svg/i--rrss--pinterest.svg' alt='Pinterest logo' />
              </a>
            </li>
            <li>
              <a href='https://wa.me/0034676081574' rel='noreferrer' target='_blank' title='whatsapp'>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src='/images/svg/i--rrss--whatsapp.svg' alt='WhatsApp logo' />
              </a>
            </li>
            <li>
              <a href='https://es-es.facebook.com/NexiaLighting/' rel='noreferrer' target='_blank' title='facebook'>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src='/images/svg/i--rrss--facebook.svg' alt='Facebook logo' />
              </a>
            </li>
          </ul>
          <p className='copyright'>Nexia S.L © {new Date().getFullYear()}</p>
        </div>
      </FooterStyled>
    </>
  )
})

const FooterStyled = styled.footer`
  background-color: var(--color-creme);
  padding: ${vw(20, 'mobile')} ${vw(24, 'mobile')};
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('nexus7')} {
    padding: ${vw(20, 'tablet')} ${vw(24, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    ${designGrid({})}
    padding: ${vw(40, 'desktop')} 0;
  }

  ${mq.greaterThan('desktop')} {
    padding: 40px 0;
  }

  .btn--contact,
  address {
    ${getP18()}
  }

  .btn--contact {
    display: block;
    margin-bottom: ${vw(20, 'mobile')};
    opacity: .4;
    text-align: left;
    transition: 250ms opacity ease-in;
    width: auto;

    &:hover {
      opacity: 1;
    }

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(40, 'desktop')};
    }
  }

  address {
    align-items: flex-start;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${vw(defaultGrid.mobile.gutter.width, 'mobile')};
    width: 100%;

    ${mq.greaterThan('nexus7')} {
      grid-column-gap: ${vw(defaultGrid.tablet.gutter.width, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      grid-column-gap: ${vw(defaultGrid.desktop.gutter.width, 'desktop')};
    }

    ${mq.greaterThan('desktop-xs')} {
      grid-column-gap: ${defaultGrid.desktop.gutter.width}px;
    }

    a {
      display: inline-block;
      grid-row: auto / span 1;
      margin-bottom: ${vw(22, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        margin-bottom: ${vw(22, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        margin-bottom: ${vw(25, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin-bottom: 25px;
      }

      &:first-child,
      &:nth-child(2) {
        grid-column: 1 / span 1;
      }

      &:last-child {
        grid-column: 2 / span 1;
        grid-row: 1 / span 2;
      }

      span {
        &:last-child {
          ${createUnderline({})}
        }
      }

      &:hover {
        span {
          background-size: 100% 1px;
        }
      }
    }
  }

  section {
    &[aria-labelledby='footer-contact'] {
      ${mq.greaterThan('tablet')} {
        grid-column: 1 / span 6;
      }
    }
  }

  nav {
    &[aria-labelledby='footer-nav-0'] {
      ${mq.greaterThan('tablet')} {
        grid-column: auto / span 6;
        margin-top: calc(2.8rem + ${vw(40, 'desktop')});
      }
    }
  }

  dl {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;

    ${mq.greaterThan('tablet')} {
      flex-wrap: nowrap;
      justify-content: space-between;
    }

    div {
      display: flex;
      flex-direction: column;
      width: 50%;

      ${mq.greaterThan('tablet')} {
        width: auto;
      }
    }

    a {
      ${createUnderline({})}
      display: block;
      padding: 2px 0;
    }
  }

  > div {
    align-items: flex-start;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${vw(defaultGrid.mobile.gutter.width/2, 'mobile')};
    width: 100%;

    ${mq.greaterThan('nexus7')} {
      grid-column-gap: ${vw(defaultGrid.tablet.gutter.width/2, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      align-items: center;
      display: flex;
      flex-wrap: nowrap;
      grid-column: 1 / span 12;
      grid-column-gap: 0;
      min-height: 48px;
    }

    .list--rrss,
    .list--langs {
      grid-column: 2 / span 1;
    }

    .list--rrss {
      align-items: flex-start;
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      grid-row: 1 / span 1;
      justify-content: flex-start;
      transform: translateY(-20px);

      ${mq.greaterThan('tablet')} {
        align-items: center;
        flex-wrap: nowrap;
        gap: 0;
        height: 100%;
        order: 3;
        transform: translateY(0);
      }

      img {
        height: 100%;
        object-fit: contain;
        opacity: .7;
        transition: 250ms opacity linear;
        width: 100%;
      }

      a {
        align-items: center;
        display: flex;
        height: ${vw(20, 'mobile')};
        justify-content: center;
        padding: 0 4px;
        width: ${vw(25, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          height: ${vw(20, 'tablet')};
          width: ${vw(25, 'tablet')};
        }

        ${mq.greaterThan('tablet')} {
          height: ${vw(20, 'desktop')};
          padding: 0 10px;
          width: ${vw(50, 'desktop')};
        }
      }

      a {
        &:hover {
          img {
            opacity: 1;
          }
        }
      }
    }

    .list--langs {
      align-items: flex-start;
      display: flex;
      gap: 10px;
      grid-row: 2 / span 1;
      justify-content: flex-start;

      ${mq.greaterThan('tablet')} {
        align-items: center;
        height: 100%;
        margin-left: ${vw(defaultGrid.desktop.gutter.width/2, 'desktop')};
        margin-right: auto;
        order: 2;
      }

      a {
        align-items: center;
        display: flex;
        justify-content: center;
        opacity: .7;
        padding: 5px;
        transition: 250ms opacity linear;

        &:hover,
        &.active {
          opacity: 1;
        }
      }
    }

    .copyright {
      grid-column: 1 / span 1;
      grid-row: 2 / span 1;
      flex-shrink: 0;
      width: 100%;

      ${mq.greaterThan('tablet')} {
        order: 1;
        width: 50%;
      }
    }
  }
`
